/**
 * Lost powerbanks summary (ChartUserByCountry style).
 * Donut: other lost vs lost in selected period; center label + list with LinearProgress.
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

function sumValues(obj) {
  if (!obj || typeof obj !== 'object') return 0;
  return Object.values(obj).reduce((a, b) => a + (Number(b) || 0), 0);
}

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: { variant: 'primary' },
      style: { fontSize: theme.typography.h5.fontSize, fontWeight: theme.typography.h5.fontWeight },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: { fontSize: theme.typography.body2.fontSize, fontWeight: theme.typography.body2.fontWeight },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;
  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

export default function LostPowerbanksSummaryDonut({ dataStatuses, lostPowerBanks, title = 'Lost summary' }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const totalLost = Number(dataStatuses?.lost) ?? 0;
  const lostInPeriod = useMemo(() => sumValues(lostPowerBanks), [lostPowerBanks]);
  const otherLost = Math.max(0, totalLost - lostInPeriod);

  const chartData = useMemo(() => {
    const d = [];
    if (otherLost > 0) d.push({ id: 'other', value: otherLost, label: t('dashboard.powerbanksOtherTotalLost') });
    if (lostInPeriod > 0) d.push({ id: 'period', value: lostInPeriod, label: t('dashboard.powerbanksLostInPeriod') });
    return d;
  }, [otherLost, lostInPeriod, t]);

  const palette = [theme.palette.grey[500], theme.palette.warning.main];

  const listItems = useMemo(() => {
    const total = totalLost || 1;
    const items = [];
    if (otherLost > 0) {
      items.push({
        key: 'other',
        name: t('dashboard.powerbanksOtherTotalLost'),
        value: otherLost,
        percent: Math.round((otherLost / total) * 100),
        color: palette[0],
      });
    }
    if (lostInPeriod > 0) {
      items.push({
        key: 'period',
        name: t('dashboard.powerbanksLostInPeriod'),
        value: lostInPeriod,
        percent: Math.round((lostInPeriod / total) * 100),
        color: palette[1],
      });
    }
    return items;
  }, [otherLost, lostInPeriod, totalLost, palette, t]);

  const pieSeriesData = chartData.length > 0 ? chartData : [{ id: 'none', value: 1, label: '—' }];

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={palette}
            margin={{ left: 80, right: 80, top: 80, bottom: 80 }}
            series={[
              {
                data: pieSeriesData,
                innerRadius: 75,
                outerRadius: 100,
                paddingAngle: 0,
                highlightScope: { fade: 'global', highlight: 'item' },
              },
            ]}
            height={260}
            width={260}
            hideLegend
          >
            <PieCenterLabel primaryText={String(totalLost)} secondaryText={t('dashboard.powerbanksTotal')} />
          </PieChart>
        </Box>
        {listItems.map((item) => (
          <Stack key={item.key} direction="row" sx={{ alignItems: 'center', gap: 2, pb: 2 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: item.color,
                flexShrink: 0,
              }}
            />
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {item.value} ({item.percent}%)
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label={item.name}
                value={Math.min(100, item.percent)}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: item.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

LostPowerbanksSummaryDonut.propTypes = {
  dataStatuses: PropTypes.shape({
    lost: PropTypes.number,
  }),
  lostPowerBanks: PropTypes.object,
  title: PropTypes.string,
};
